import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { Link } from 'react-router-dom';
import qs from 'qs';
import connect from '../store/connect';
import RenderPrice from './RenderPrice';
import { color, size } from '../styles/helpers/variables';
import { toCurrency } from '../util/toCurrency';
import { fontFamilyBody } from '../styles/helpers/mixins';
import { SITE_ROOT_URL } from '../constants/endpoints';

const getProductUrlWithOptions = (baseUrl, options) => {
  if (options.size) {
    const queryObject = options.reduce(
      (memory, option) => Object.assign(memory, { [option.get('configurationTypeName')]: option.get('id') }),
      {}
    );
    return `${baseUrl}?${qs.stringify(queryObject)}`;
  }
  return baseUrl;
};

const CartItem = props => {
  const { cartRow } = props;
  const currentProduct = cartRow.get('product');
  const currentOptions = cartRow.get('options');
  const quantity = cartRow.get('quantity');
  const productOptions = currentOptions
    .map(option => (
      <CartOptionsContainer key={option.get('id')}>
        <CartItemImage small src={SITE_ROOT_URL + option.getIn(['images', 0, 'url'])} />
        <CartItemContent>
          <CartItemTextContainer>
            <CartItemTitle>{`${option.get('configurationTypeName')}: ${option.get('name')}`}</CartItemTitle>
            {option.get('price') ? (
              <CartItemText>
                Pris:{' '}
                <RenderPrice
                  inline
                  price={option.get('price')}
                  originalPrice={option.get('originalPrice')}
                  fontWeight="normal"
                  fontSize="12px"
                />
              </CartItemText>
            ) : (
              false
            )}
          </CartItemTextContainer>
        </CartItemContent>
      </CartOptionsContainer>
    ))
    .toArray();

  const cartRowUrl = getProductUrlWithOptions(currentProduct.get('url'), currentOptions);
  return (
    <CartItemRootContainer>
      <CartItemContainer>
        {!props.modifyDisabled ? <CloseButton onClick={() => props.deleteCartRow(cartRow)} /> : false}
        <CartItemImage src={SITE_ROOT_URL + currentProduct.getIn(['mainImage', 'url'])} />
        <CartItemContent>
          <CartItemTextContainer>
            <CartItemTitleLink to={cartRowUrl}>{currentProduct.get('name')}</CartItemTitleLink>

            {/* <CartItemText>{currentProduct.get('name')}</CartItemText> */}

            <CartItemText>
              Pris:{' '}
              <RenderPrice
                inline
                price={currentProduct.get('price')}
                originalPrice={currentProduct.get('originalPrice')}
                fontWeight="normal"
                fontSize="12px"
              />
            </CartItemText>
            <CartItemText>Leverans: {currentProduct.get('deliveryInformation')}</CartItemText>
          </CartItemTextContainer>
          <CartItemMetaContainer>
            <CartItemMetaQuantityContainer>
              {!props.modifyDisabled ? (
                <CartItemButton
                  disabled={quantity - 1 <= 0 || props.modifyDisabled}
                  onClick={() => props.removeCartItem(cartRow)}
                >
                  -
                </CartItemButton>
              ) : (
                false
              )}
              <CartItemQuantity>{quantity}</CartItemQuantity>
              {!props.modifyDisabled ? (
                <CartItemButton
                  disabled={props.modifyDisabled}
                  onClick={() => props.addCartItem(currentProduct.get('id'), currentOptions)}
                >
                  +
                </CartItemButton>
              ) : (
                false
              )}
            </CartItemMetaQuantityContainer>
            <CartItemPrice>{toCurrency(cartRow.get('totalPrice'))}</CartItemPrice>
          </CartItemMetaContainer>
        </CartItemContent>
      </CartItemContainer>
      {currentOptions.size ? <CartItemOptionsContainer>{productOptions}</CartItemOptionsContainer> : false}
    </CartItemRootContainer>
  );
};

const CartItemRootContainer = styled.div`
  background-color: ${color.white};
  padding-bottom: 2px;
  margin-bottom: 3px;
`;

const CartItemContainer = styled.div`
  position: relative;
  background-color: ${color.white};
  padding: 12px 15px 0 10px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: 2px;

  @media screen and (min-width: ${size.xLarge}px) {
    padding: 20px 38px 12px 12px;
    align-items: center;
  }
`;

const CartItemOptionsContainer = styled.div`
  border-left: 1px solid ${color.grey};
  margin: 10px 0 10px 30px;
`;

const CartOptionsContainer = CartItemContainer.extend`
  min-height: 40px;
  padding: 2px 15px 2px 10px;
  align-items: center;

  @media screen and (min-width: ${size.xLarge}px) {
    padding: 4px 38px 4px 12px;
    margin-bottom: 3px;
  }
`;

const CloseButton = styled.button`
  height: 30px;
  width: 30px;
  position: absolute;
  top: 0;
  right: 0;
  border: none;
  background-color: transparent;
  background-image: url('/images/icons/close.svg');
  background-position: center;
  background-repeat: no-repeat;
  cursor: pointer;
  background-size: 10px;
  font-size: 20px;
`;

const CartItemImage = styled.img`
  width: 35px;
  height: auto;
  max-height: 35px;
  object-fit: contain;
  margin-top: 6px;

  @media screen and (min-width: ${size.xLarge}px) {
    width: 50px;
    max-height: 50px;
    margin-top: 0;
  }

  ${props =>
    props.small &&
    css`
      width: 30px;
      max-height: 30px;

      @media screen and (min-width: ${size.xLarge}px) {
        width: 40px;
        max-height: 40px;
      }
    `};
`;

const CartItemContent = styled.div`
  flex: 1;
  padding-left: 16px;
  display: flex;
  flex-direction: column;

  @media screen and (min-width: ${size.xLarge}px) {
    flex-direction: row;
    padding-left: 26px;
  }
`;

const CartItemTitleLink = styled(Link)`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  text-decoration: none;
  color: ${color.black};
  transition: opacity 250ms ease-out;

  &:hover {
    opacity: 0.8;
  }

  @media screen and (min-width: ${size.xLarge}px) {
    font-size: 16px;
  }
`;

const CartItemTitle = CartItemTitleLink.withComponent('p').extend`
  font-size: 12px;

  @media screen and (min-width: ${size.xLarge}px) {
    font-size: 14px;
  }
`;

const CartItemText = styled.div`
  margin: 0.08725em 0 0.08725em;
  font-size: 12px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.4;
  letter-spacing: normal;
  text-align: left;
  color: ${color.black};
  ${props =>
    props.fontWeight &&
    css`
      font-weight: ${props.fontWeight};
    `};
  ${props =>
    props.marginBottom &&
    css`
      margin-bottom: ${props.marginBottom};
    `};
`;

const CartItemTextContainer = styled.div`
  ${props =>
    props.padding &&
    css`
      padding: ${props.padding};
    `};
  @media screen and (min-width: ${size.xLarge}px) {
    flex: 1;
  }
`;

const CartItemMetaContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: 10px 0;
  position: relative;
  left: -10px;

  @media screen and (min-width: ${size.xLarge}px) {
    position: initial;
    margin: 0;
    height: 40px;
  }
`;

const CartItemMetaQuantityContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
`;

const CartItemQuantity = styled.div`
  min-width: 30px;
  height: 30px;
  border-radius: 15px;
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${color.grey};
  font-size: 14px;
  font-weight: 600;
  font-style: normal;
  font-stretch: normal;
  line-height: 1;
  letter-spacing: normal;
  text-align: center;
  color: ${color.black};
`;

const CartItemButton = styled.button`
  ${fontFamilyBody};
  outline: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  font-size: 24px;
  font-weight: 300;
  font-style: normal;
  font-stretch: normal;
  line-height: 0.92;
  letter-spacing: normal;
  text-align: left;
  cursor: pointer;
  padding: 2px 8px;
  top: -2px;
  position: relative;
  color: ${color.black};
  transition: opacity 250ms ease-out;

  &:disabled {
    color: ${color.darkgrey};
    cursor: default;
  }

  &:hover {
    opacity: 0.6;
  }
`;

const CartItemPrice = styled.p`
  display: inline-block;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.69;
  letter-spacing: normal;
  text-align: left;
  color: ${color.black};
  margin-left: 10px;
  min-width: 70px;
  text-align: right;
`;

CartItem.defaultProps = {
  cartRow: Map(),
  modifyDisabled: false,
  addCartItem: () => {},
  removeCartItem: () => {},
  deleteCartRow: () => {}
};

CartItem.propTypes = {
  cartRow: PropTypes.instanceOf(Map),
  addCartItem: PropTypes.func,
  removeCartItem: PropTypes.func,
  deleteCartRow: PropTypes.func,
  modifyDisabled: PropTypes.bool
};

export default connect(CartItem, {}, store => ({
  viewstate: store.viewstate
}));



// WEBPACK FOOTER //
// ./src/components/CartItem.js